import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSession, useUser, useIsAuthLoading } from '@/hooks/useAuth'
import { useMembership } from '@/hooks/useMembership'
import { supabase } from '@/lib/supabase/client'
import TopNav from '@/components/layout/TopNav'
import Footer from '@/components/landing/Footer'

// Routes where the profile-completeness gate is intentionally skipped.
const PROFILE_GATE_BYPASS = new Set(['/setup', '/join', '/welcome', '/onboarding/create-village'])

// Routes where the village-membership gate is intentionally skipped.
const VILLAGE_GATE_BYPASS = new Set(['/setup', '/join', '/welcome', '/onboarding/create-village'])

export default function ProtectedRoute() {
  const session = useSession()
  const isAuthLoading = useIsAuthLoading()
  const user = useUser()
  const location = useLocation()

  // Profile gate: redirect to /setup if display_name is blank.
  // staleTime: Infinity — Setup.tsx writes to this cache after upsert.
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-gate', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user!.id)
        .single()
      return data
    },
    enabled: !!user,
    staleTime: Infinity,
    retry: 1,
  })

  // Village gate: redirect to /welcome if the user has no village membership.
  // staleTime: Infinity — useCreateVillage invalidates ['membership'] on success.
  const { data: membership, isLoading: membershipLoading } = useMembership()

  const emailPrefix = user?.email ? user.email.split('@')[0] : ''
  const needsSetup =
    !!user &&
    !profileLoading &&
    (!profile?.display_name || profile.display_name === emailPrefix)

  const inProfileBypass = PROFILE_GATE_BYPASS.has(location.pathname)
  const inVillageBypass = VILLAGE_GATE_BYPASS.has(location.pathname)

  // Hold render until auth, profile, and membership are resolved.
  if (isAuthLoading || (!!user && (profileLoading || (!inVillageBypass && membershipLoading)))) {
    return null
  }

  if (!session) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />
  }

  if (needsSetup && !inProfileBypass) {
    return <Navigate to="/setup" state={{ from: location }} replace />
  }

  const hasVillage = membership?.villageId != null
  if (!hasVillage && !inVillageBypass) {
    return <Navigate to="/welcome" replace />
  }

  return (
    <div className="flex flex-col" style={{ minHeight: '100dvh', background: 'var(--paper)' }}>
      <TopNav />
      <div style={{ flex: 1 }}>
        <Outlet />
      </div>
      <Footer />
    </div>
  )
}
